import React, { useState, useEffect } from 'react';
import { db } from '../../services/firebase';
import { collection, onSnapshot, query, orderBy, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import styles from '../../styles/AdminStyles/AdminSeatedList.module.css';
import { useNavigate } from 'react-router-dom';

const AdminSeatedList = () => {
    const [seatedList, setSeatedList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const q = query(collection(db, 'seatedCustomers'), orderBy('seatedAt', 'desc'));
        const unsubscribe = onSnapshot(q, 
            (snapshot) => {
                const list = snapshot.docs.map(doc => {
                    const data = doc.data();
                    // Remove the embedded id field to avoid confusion
                    const { id: embeddedId, ...cleanData } = data;
                    return { 
                        id: doc.id, // Use the real Firebase document ID
                        ...cleanData 
                    };
                });
                setSeatedList(list);
                setLoading(false);
            },
            (err) => {
                console.error("Error fetching seated list:", err);
                setError("Failed to load seated customers list.");
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, []);

    const handleClearCustomer = async (id) => {
        if (!currentUser) {
            alert('You must be logged in to perform this action.');
            return;
        }
        
        if (!currentUser.isAdmin) {
            alert('You must be an admin to clear customers from the seated list.');
            return;
        }
        
        if (window.confirm('Are you sure you want to clear this party from the seated list?')) {
            try {
                await deleteDoc(doc(db, 'seatedCustomers', id));
                
                // Check if document still exists after deletion
                const checkDoc = await getDoc(doc(db, 'seatedCustomers', id));
                if (checkDoc.exists()) {
                    alert('Failed to delete: Document still exists. Check Firestore security rules.');
                } else {
                    // Manual UI update as backup
                    setSeatedList(prevList => prevList.filter(item => item.id !== id));
                }
            } catch (err) {
                console.error("Error clearing document: ", err);
                alert(`Failed to clear from seated list. Error: ${err.message}`);
            }
        }
    };

    const handleClearAll = async () => {
        if (!currentUser) {
            alert('You must be logged in to perform this action.');
            return;
        }
        
        if (!currentUser.isAdmin) {
            alert('You must be an admin to clear all customers from the seated list.');
            return;
        }

        if (seatedList.length === 0) {
            alert('No customers to clear.');
            return;
        }

        const confirmMessage = `Are you sure you want to clear ALL ${seatedList.length} customers from the seated list? This cannot be undone.`;
        
        if (window.confirm(confirmMessage)) {
            try {
                // Delete all documents
                const deletePromises = seatedList.map(customer => 
                    deleteDoc(doc(db, 'seatedCustomers', customer.id))
                );
                
                await Promise.all(deletePromises);
                
                // Clear the UI immediately
                setSeatedList([]);
                
                alert('All customers have been cleared from the seated list.');
            } catch (err) {
                console.error("Error clearing all documents: ", err);
                alert(`Failed to clear all customers. Error: ${err.message}`);
            }
        }
    };

    if (loading) {
        return <div className={styles.centered}>Loading seated customers...</div>;
    }

    if (error) {
        return <div className={`${styles.centered} ${styles.error}`}>{error}</div>;
    }

    return (
        <div className={styles.container}>
            <button onClick={() => navigate('/admin/waiting-list')} className={styles.navigateButton} style={{marginBottom: 16, padding: '8px 16px', fontWeight: 600, borderRadius: 6, background: '#e3e8f0', border: 'none', cursor: 'pointer'}}>Go to Waiting List</button>
            <div className={styles.header}>
                <h1 className={styles.title}>Seated Customers Log</h1>
                <div className={styles.buttonGroup}>
                    {seatedList.length > 0 && (
                        <button 
                            onClick={handleClearAll}
                            className={styles.clearAllButton}
                        >
                            Clear All ({seatedList.length})
                        </button>
                    )}
                </div>
            </div>
            
            {seatedList.length === 0 ? (
                <p className={styles.emptyMessage}>No customers have been seated yet.</p>
            ) : (
                <>
                    {/* Desktop Table View */}
                    <div className={styles.desktopView}>
                        <div className={styles.tableContainer}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Phone</th>
                                        <th>Party Size</th>
                                        <th>Table No.</th>
                                        <th>Time Seated</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {seatedList.map((entry) => (
                                        <tr key={entry.id}>
                                            <td>{entry.name}</td>
                                            <td>{entry.phone}</td>
                                            <td>{entry.numPeople}</td>
                                            <td>{entry.tableNumber}</td>
                                            <td>{entry.seatedAt?.toDate().toLocaleTimeString()}</td>
                                            <td>
                                                <button 
                                                    onClick={() => handleClearCustomer(entry.id)}
                                                    className={styles.clearButton}
                                                >
                                                    Clear
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Mobile Card View */}
                    <div className={styles.mobileView}>
                        {seatedList.map((entry) => (
                            <div key={entry.id} className={styles.mobileCard}>
                                <div className={styles.cardHeader}>
                                    <h3 className={styles.customerName}>{entry.name}</h3>
                                    <span className={styles.tableNumber}>Table {entry.tableNumber}</span>
                                </div>
                                <div className={styles.cardBody}>
                                    <div className={styles.cardRow}>
                                        <span className={styles.label}>Phone:</span>
                                        <span className={styles.value}>{entry.phone}</span>
                                    </div>
                                    <div className={styles.cardRow}>
                                        <span className={styles.label}>Party Size:</span>
                                        <span className={styles.value}>{entry.numPeople}</span>
                                    </div>
                                    <div className={styles.cardRow}>
                                        <span className={styles.label}>Seated At:</span>
                                        <span className={styles.value}>{entry.seatedAt?.toDate().toLocaleTimeString()}</span>
                                    </div>
                                </div>
                                <div className={styles.cardActions}>
                                    <button 
                                        onClick={() => handleClearCustomer(entry.id)}
                                        className={styles.clearButtonMobile}
                                    >
                                        Clear from Seated List
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminSeatedList; 